"use client";

import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import Flex from "~/components/flex/Flex";
import ResetPasswordModal from "~/components/modal/ResetPasswordModal";
import RegistrationModal from "~/app/[locale]/auth/_components/RegistrationModal";
import {
  SEARCH_PARAM_PQ_INGESTION_FILE_UPLOAD_MODAL,
  SEARCH_PARAM_REGISTRATION_MODAL,
  SEARCH_PARAM_REGISTRATION_REQUEST_SUBMITTED_MODAL,
  SEARCH_PARAM_RESET_PASSWORD_MODAL,
  SEARCH_PARAM_CREATE_SINGLE_OFFER_MODAL,
  SEARCH_PARAM_UPDATE_SINGLE_OFFER_MODAL,
} from "~/constants";
import { QUERY_PARAM_POFE_DETAILS_MODAL } from "~/constants/queryParams";
import POFEDetailsModal from "../../(app)/orders/_components/POFEDetailsModal";
import PQIngestionFileUploadModal from "../../(app)/pq-ingestion-files/_components/PQIngestionFileUploadModal";
import RegistrationRequestSubmittedModal from "../../auth/_components/RegistrationRequestSubmittedModal";
import CreateSingleOfferModal from "../../(app)/stock-list/_components/CreateSingleOfferModal";
import UpdateSingleOfferModal from "../../(app)/stock-list/_components/UpdateSingleOfferModal";

interface ModalConfig {
  paramKey: string;
  component: () => React.ReactNode;
  paramValue?: string; // Optional specific value to match
  matchAnyValue?: boolean; // When true, match if param exists with any non-empty value (e.g. for id-based modals)
}

const MODAL_CONFIGS: Record<string, ModalConfig> = {
  [QUERY_PARAM_POFE_DETAILS_MODAL]: {
    paramKey: QUERY_PARAM_POFE_DETAILS_MODAL,
    component: () => <POFEDetailsModal />,
  },
  [SEARCH_PARAM_RESET_PASSWORD_MODAL]: {
    paramKey: SEARCH_PARAM_RESET_PASSWORD_MODAL,
    component: () => <ResetPasswordModal />,
  },
  [SEARCH_PARAM_REGISTRATION_MODAL]: {
    paramKey: SEARCH_PARAM_REGISTRATION_MODAL,
    component: () => <RegistrationModal />,
  },
  [SEARCH_PARAM_REGISTRATION_REQUEST_SUBMITTED_MODAL]: {
    paramKey: SEARCH_PARAM_REGISTRATION_REQUEST_SUBMITTED_MODAL,
    component: () => <RegistrationRequestSubmittedModal />,
  },
  [SEARCH_PARAM_PQ_INGESTION_FILE_UPLOAD_MODAL]: {
    paramKey: SEARCH_PARAM_PQ_INGESTION_FILE_UPLOAD_MODAL,
    component: () => <PQIngestionFileUploadModal />,
  },
  [SEARCH_PARAM_CREATE_SINGLE_OFFER_MODAL]: {
    paramKey: SEARCH_PARAM_CREATE_SINGLE_OFFER_MODAL,
    component: () => <CreateSingleOfferModal />,
  },
  [SEARCH_PARAM_UPDATE_SINGLE_OFFER_MODAL]: {
    paramKey: SEARCH_PARAM_UPDATE_SINGLE_OFFER_MODAL,
    component: () => <UpdateSingleOfferModal />,
    matchAnyValue: true,
  },
} as const;

/**
 * Type definition for the currently active modal
 * @interface ActiveModal
 * @property {keyof typeof MODAL_CONFIGS} type - The type of modal that is currently active
 * @property {() => React.ReactNode} renderModal - Function that renders the active modal component
 */
type ActiveModal = {
  type: keyof typeof MODAL_CONFIGS;
  renderModal: () => React.ReactNode;
};

const ANIMATION_DURATION = 250; // ms

/**
 * DisplayModals Component
 *
 * A dynamic modal display system that manages multiple types of modals based on URL search parameters.
 * This component handles the rendering and animation of various modals throughout the application,
 * including but not limited to:
 * - Request Quote Modal
 * - View Summary Modal
 * - Change Address Modal
 * - Password-related Modals
 * - Cart-related Modals
 * - Product Detail Modals
 *
 * The component uses URL search parameters to determine which modal to display and manages
 * the visibility state with smooth transitions.
 */
const DisplayModals = () => {
  const searchParams = useSearchParams();
  const [isVisible, setIsVisible] = useState(false);
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);

  const matchedModal = useMemo(() => {
    for (const [type, config] of Object.entries(MODAL_CONFIGS)) {
      const value = searchParams.get(config.paramKey);
      if (!value) continue;
      if (
        config.matchAnyValue ||
        (!config.paramValue && value === "true") ||
        (config.paramValue && value === config.paramValue)
      ) {
        return { type, renderModal: config.component };
      }
    }
    return null;
  }, [searchParams]);

  useEffect(() => {
    setActiveModal(matchedModal);
  }, [matchedModal]);

  useEffect(() => {
    if (activeModal) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => setActiveModal(null), ANIMATION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [activeModal]);

  if (!activeModal) return null;

  return (
    <Flex
      className={`fixed inset-0 z-90 transition-opacity duration-200 ease-in-out ${isVisible ? "opacity-100" : "opacity-0"
        }`}
      aria-hidden={!isVisible}
    >
      {activeModal?.renderModal()}
    </Flex>
  );
};

export default DisplayModals;
